import {useOverlayActivity} from "components/useOverlayActivity";
import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import styled, {css, keyframes} from "styled-components";
import {fontFamilies, radii} from "theme/tokens";

/**
 * Non-modal hover/click dropdown primitive for the top navigation (ui#111).
 *
 * WHY THIS EXISTS: the Resources menu was a MUI `<Menu>`, which renders a full-screen
 * `.MuiBackdrop-root` (`position:fixed; inset:0; pointer-events:auto`). On hover-open the backdrop
 * covered the trigger, so the trigger's `mouseleave` fired → the menu closed → the backdrop was
 * removed → `mouseenter` → it reopened… an infinite flicker (OPEN/closed oscillation while the cursor
 * sat still). This primitive has NO backdrop and NO portal: the trigger and the panel live inside ONE
 * relatively-positioned wrapper ({@link DropdownRoot}), so the panel is a DOM descendant of the hover
 * region and moving the cursor from the trigger into the panel never leaves it — the flicker is
 * structurally impossible. Outside clicks are caught by a document `pointerdown` listener we own (not
 * a backdrop); Escape closes and restores focus to the trigger.
 */

/** Hover-intent timing: a short beat before opening (so a passing cursor doesn't flash the panel) and
 *  a slightly longer one before closing (so a brief exit/re-entry doesn't dismiss it). */
const HOVER_OPEN_DELAY = 90;
const HOVER_CLOSE_DELAY = 140;

/** Arm hover only on devices that truly hover with a fine pointer; touch falls back to tap-to-open. */
function hoverCapable(): boolean {
	return typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

export interface UseHoverMenuOptions {
	/** Enable pointer hover-intent open/close (desktop). Touch always uses tap. Default `true`. */
	hover?: boolean;
}

export interface HoverMenu {
	/** Whether the panel is currently open. */
	open: boolean;
	/** Close immediately (clears any pending hover timers). */
	close: () => void;
	/** Attach to the wrapping {@link DropdownRoot}. */
	containerRef: React.RefObject<HTMLDivElement | null>;
	/** Attach to the trigger `<button>` (Escape restores focus here). */
	triggerRef: React.RefObject<HTMLButtonElement | null>;
	/** Spread on the wrapping {@link DropdownRoot} (hover region + Escape). */
	containerProps: {
		onMouseEnter?: () => void;
		onMouseLeave?: () => void;
		onKeyDown: (event: React.KeyboardEvent) => void;
	};
	/** Spread on the trigger `<button>` (click-toggle + ARIA). */
	triggerProps: {
		onClick: () => void;
		"aria-expanded": boolean;
	};
}

/**
 * Behaviour for a non-modal nav dropdown: open state, hover-intent (desktop) or tap/click (touch),
 * outside-click-to-close (watched on the document, since there is no backdrop) and Escape-to-close
 * with focus restored to the trigger.
 */
export function useHoverMenu(options: UseHoverMenuOptions = {}): HoverMenu {
	const {hover = true} = options;
	const [open, setOpen] = useState(false);
	useOverlayActivity(open, undefined, false);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const openTimer = useRef<number | null>(null);
	const closeTimer = useRef<number | null>(null);

	const clearOpenTimer = () => {
		if (openTimer.current !== null) {
			window.clearTimeout(openTimer.current);
			openTimer.current = null;
		}
	};
	const clearCloseTimer = () => {
		if (closeTimer.current !== null) {
			window.clearTimeout(closeTimer.current);
			closeTimer.current = null;
		}
	};

	const close = useCallback(() => {
		clearOpenTimer();
		clearCloseTimer();
		setOpen(false);
	}, []);

	// Clear any pending timers on unmount.
	useEffect(
		() => () => {
			clearOpenTimer();
			clearCloseTimer();
		},
		[],
	);

	// No backdrop ⇒ we watch the document ourselves: a pointerdown outside the wrapper closes the panel.
	useEffect(() => {
		if (!open) return;
		const onPointerDown = (event: PointerEvent) => {
			if (!containerRef.current?.contains(event.target as Node)) {
				close();
			}
		};
		document.addEventListener("pointerdown", onPointerDown, true);
		return () => document.removeEventListener("pointerdown", onPointerDown, true);
	}, [open, close]);

	const onMouseEnter = useCallback(() => {
		if (!hoverCapable()) return;
		clearCloseTimer();
		if (openTimer.current === null && !open) {
			openTimer.current = window.setTimeout(() => {
				openTimer.current = null;
				setOpen(true);
			}, HOVER_OPEN_DELAY);
		}
	}, [open]);

	const onMouseLeave = useCallback(() => {
		if (!hoverCapable()) return;
		clearOpenTimer();
		clearCloseTimer();
		closeTimer.current = window.setTimeout(() => {
			closeTimer.current = null;
			setOpen(false);
		}, HOVER_CLOSE_DELAY);
	}, []);

	const onClick = useCallback(() => {
		clearOpenTimer();
		clearCloseTimer();
		setOpen((current) => !current);
	}, []);

	const onKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === "Escape" && open) {
				event.stopPropagation();
				close();
				triggerRef.current?.focus();
			}
		},
		[open, close],
	);

	return {
		open,
		close,
		containerRef,
		triggerRef,
		containerProps: hover ? {onMouseEnter, onMouseLeave, onKeyDown} : {onKeyDown},
		triggerProps: {onClick, "aria-expanded": open},
	};
}

/**
 * Wrapper that scopes the panel's absolute position AND unifies the hover region: because the panel
 * is a DOM descendant, moving the cursor from the trigger into the panel never leaves this element
 * (mouseleave only fires when the pointer exits the whole subtree) — so there is no flicker.
 */
export const DropdownRoot = styled.div`
	position: relative;
	display: inline-flex;
	align-items: center;
`;

const panelIn = keyframes`
	from {
		opacity: 0;
		transform: translateY(-6px);
	}
	to {
		opacity: 1;
		transform: none;
	}
`;

/**
 * Absolute layer under the trigger. `top:100%` + `padding-top` forms a TRANSPARENT hover bridge so
 * there is no dead gap between the trigger's bottom edge and the visible card — the cursor crossing
 * the bridge stays inside the hover region (the padding belongs to this descendant element).
 */
export const PanelLayer = styled.div<{$align?: "left" | "right"}>`
	position: absolute;
	top: 100%;
	${({$align = "right"}) => ($align === "right" ? "right: 0;" : "left: 0;")}
	z-index: 40;
	padding-top: 8px;
	animation: ${panelIn} 0.14s cubic-bezier(0.2, 0.7, 0.2, 1);

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

/** The visible dropdown card — the same card/border/shadow language as the nav pill and popovers. */
export const Panel = styled.div`
	min-width: 210px;
	background: ${({theme}) => theme.tokens.card};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.panel};
	box-shadow: ${({theme}) => theme.tokens.shadowCard};
	padding: 6px;
	display: flex;
	flex-direction: column;
	gap: 1px;
`;

/** A hairline separator between panel groups — e.g. the legal links and the language section (ui#117). */
export const PanelDivider = styled.span`
	height: 1px;
	margin: 5px 6px;
	background: ${({theme}) => theme.tokens.cardBorder};
	flex: none;
`;

/** A row in the panel — a Resources legal link or a language choice. */
export const PanelItem = styled.button<{$selected?: boolean}>`
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
	text-align: left;
	font-family: ${fontFamilies.sans};
	font-size: 13.5px;
	font-weight: 600;
	color: ${({theme}) => theme.tokens.ink};
	background: ${({theme, $selected}) => ($selected ? theme.tokens.field : "transparent")};
	border: none;
	border-radius: 9px;
	padding: 9px 12px;
	min-height: 44px;
	cursor: pointer;
	white-space: nowrap;
	text-decoration: none;
	transition: background 0.14s;

	&:hover {
		background: ${({theme}) => theme.tokens.field};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: -2px;
	}
`;

/* ================================================================================================ */
/* Nested submenu (ui#119): a "Parent ▸" row inside a {@link Panel} that flies out to a side          */
/* {@link Panel} on desktop and expands INLINE (accordion) on touch / ≤640px — same non-backdrop      */
/* hover primitive, one continuous hover region, structurally no flicker.                             */
/* ================================================================================================ */

/** Below this width a side flyout can't fit a 375px row, so the submenu expands inline instead. */
const SUBMENU_INLINE_QUERY = "(max-width: 640px)";

/** Inline (accordion) when the pointer can't hover OR the viewport is too narrow for a side flyout. */
function submenuInlineMode(): boolean {
	if (typeof window === "undefined") return true;
	const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
	const narrow = window.matchMedia(SUBMENU_INLINE_QUERY).matches;
	return !canHover || narrow;
}

export interface SubmenuItemProps {
	/** The parent-row label (e.g. "Language") — the child rows live one level in. */
	label: React.ReactNode;
	/** Optional decorative leading icon; omitted by default so existing callers retain their layout. */
	icon?: React.ReactNode;
	/** Optional muted hint shown before the chevron (e.g. the active locale's flag). Decorative. */
	hint?: React.ReactNode;
	/** Accessible name for the submenu group (its `role="menu"`). */
	submenuLabel: string;
	/** The submenu rows — a side flyout (desktop) or inline beneath the row (touch/narrow). */
	children: React.ReactNode;
}

/**
 * A nested-submenu row for the non-backdrop {@link Panel} (ui#119). On a real pointer at desktop
 * widths it opens a side {@link Panel} flyout (right of the row, flipping LEFT when it would overflow
 * the viewport); on touch or ≤640px — where a side flyout can't fit a phone row — it expands the rows
 * INLINE as an accordion (`aria-expanded`, no horizontal overflow). The row and its flyout share ONE
 * hover region (the flyout is a DOM descendant reached across a transparent bridge), so moving the
 * cursor into it never crosses a gap and the flicker is structurally impossible — the same guarantee
 * as {@link DropdownRoot}. Hover-intent open/close is gated behind `(hover:hover) and (pointer:fine)`;
 * keyboard opens on Enter/ArrowRight, Escape/ArrowLeft close it (focus back to the row, with
 * `stopPropagation` so the parent menu's Escape doesn't also fire), and Arrow keys rove the rows. No
 * focus trap.
 */
export function SubmenuItem({label, icon, hint, submenuLabel, children}: SubmenuItemProps) {
	const [open, setOpen] = useState(false);
	// SSG default: inline. This row DOES render server-side — the Resources panel that hosts it stays
	// mounted and only CSS-hides (see NavBar) — and since #1545 so do its rows, so the pre-rendered
	// markup is the accordion. `window` is unreadable there and the effect below corrects the choice
	// on mount, before the submenu can be opened.
	const [inline, setInline] = useState(true);
	const [flip, setFlip] = useState<"left" | "right">("right");
	const rootRef = useRef<HTMLDivElement | null>(null);
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const flyoutRef = useRef<HTMLDivElement | null>(null);
	const submenuRef = useRef<HTMLDivElement | null>(null);
	const openTimer = useRef<number | null>(null);
	const closeTimer = useRef<number | null>(null);

	const clearOpenTimer = () => {
		if (openTimer.current !== null) {
			window.clearTimeout(openTimer.current);
			openTimer.current = null;
		}
	};
	const clearCloseTimer = () => {
		if (closeTimer.current !== null) {
			window.clearTimeout(closeTimer.current);
			closeTimer.current = null;
		}
	};

	// Resolve inline-vs-flyout on mount and track pointer/width changes (rotate, resize, DevTools).
	useEffect(() => {
		const update = () => setInline(submenuInlineMode());
		update();
		const queries = [
			window.matchMedia("(hover: hover) and (pointer: fine)"),
			window.matchMedia(SUBMENU_INLINE_QUERY),
		];
		queries.forEach((query) => query.addEventListener("change", update));
		return () => queries.forEach((query) => query.removeEventListener("change", update));
	}, []);

	// Clear any pending hover timers on unmount.
	useEffect(
		() => () => {
			clearOpenTimer();
			clearCloseTimer();
		},
		[],
	);

	const focusFirstRow = useCallback(() => {
		submenuRef.current?.querySelector<HTMLElement>('[role="menuitemradio"], button')?.focus();
	}, []);

	// Flyout side: flips LEFT when a right flyout would overflow the viewport's right edge. Measuring
	// the flyout's intrinsic width (`offsetWidth`, side-independent) against the space to the right of
	// the row keeps the decision stable — it never oscillates as `flip` toggles the card's position.
	// `useLayoutEffect` runs before paint, so the correct side is committed with no visible jump.
	useLayoutEffect(() => {
		if (!open || inline) {
			setFlip("right");
			return;
		}
		const root = rootRef.current;
		const fly = flyoutRef.current;
		if (!root || !fly) return;
		const spaceRight = window.innerWidth - root.getBoundingClientRect().right;
		setFlip(spaceRight >= fly.offsetWidth + 16 ? "right" : "left");
	}, [open, inline]);

	const onMouseEnter = () => {
		if (inline) return;
		clearCloseTimer();
		if (openTimer.current === null && !open) {
			openTimer.current = window.setTimeout(() => {
				openTimer.current = null;
				setOpen(true);
			}, HOVER_OPEN_DELAY);
		}
	};

	const onMouseLeave = () => {
		if (inline) return;
		clearOpenTimer();
		clearCloseTimer();
		closeTimer.current = window.setTimeout(() => {
			closeTimer.current = null;
			setOpen(false);
		}, HOVER_CLOSE_DELAY);
	};

	const onTriggerClick = () => {
		clearOpenTimer();
		clearCloseTimer();
		setOpen((current) => !current);
	};

	const onTriggerKeyDown = (event: React.KeyboardEvent) => {
		if (
			event.key === "ArrowRight" ||
			event.key === "Enter" ||
			event.key === " " ||
			(event.key === "ArrowDown" && open)
		) {
			event.preventDefault();
			clearOpenTimer();
			clearCloseTimer();
			setOpen(true);
			window.requestAnimationFrame(focusFirstRow);
		}
	};

	/**
	 * Picking a row collapses the submenu, like any menu. Load-bearing since ui#812: the hosting
	 * Resources panel only CSS-hides on close (it stays mounted for SSG/hover), so an accordion left
	 * expanded would greet the panel's next visit stale — and the next tap on the parent row would
	 * COLLAPSE it, reading as a dead control. The old ProfileMenu host masked this by unmounting.
	 */
	const onSubmenuClick = (event: React.MouseEvent) => {
		if ((event.target as HTMLElement).closest('[role="menuitemradio"], button') !== null) {
			clearOpenTimer();
			clearCloseTimer();
			setOpen(false);
		}
	};

	const onSubmenuKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Escape" || event.key === "ArrowLeft") {
			event.preventDefault();
			// Close the submenu FIRST; the parent menu's Escape handler must not also fire (ui#119).
			event.stopPropagation();
			clearOpenTimer();
			clearCloseTimer();
			setOpen(false);
			triggerRef.current?.focus();
			return;
		}
		if (event.key === "ArrowDown" || event.key === "ArrowUp") {
			event.preventDefault();
			const rows = Array.from(
				submenuRef.current?.querySelectorAll<HTMLElement>('[role="menuitemradio"], button') ?? [],
			);
			if (rows.length === 0) return;
			const index = rows.indexOf(document.activeElement as HTMLElement);
			const delta = event.key === "ArrowDown" ? 1 : -1;
			rows[(index + delta + rows.length) % rows.length]?.focus();
		}
	};

	return (
		<SubmenuRoot
			ref={rootRef}
			onMouseEnter={inline ? undefined : onMouseEnter}
			onMouseLeave={inline ? undefined : onMouseLeave}
		>
			<PanelItem
				ref={triggerRef}
				type="button"
				aria-haspopup="menu"
				aria-expanded={open}
				$selected={open && !inline}
				onClick={onTriggerClick}
				onKeyDown={onTriggerKeyDown}
			>
				{icon}
				<SubmenuLabel>{label}</SubmenuLabel>
				{hint != null && <SubmenuHint aria-hidden="true">{hint}</SubmenuHint>}
				<Chevron $down={open && inline} aria-hidden="true">
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
						 strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
						<path d="M9 6l6 6-6 6"/>
					</svg>
				</Chevron>
			</PanelItem>
			{/* Mounted whether or not it is open, and CSS-hidden when closed — the same thing the
			    hosting Resources panel does, and for the same reason (#1545): the rows carry the only
			    anchors that link transferify.cloud and transferify.ro to each other, and a crawler
			    reading the pre-rendered HTML never opens a menu. Both layouts hide with `display:none`,
			    which is the only form that takes the closed submenu out of LAYOUT as well as out of
			    sight — see the note on FlyoutLayer for what `visibility:hidden` cost here. */}
			{inline ? (
				<InlineSubmenu ref={submenuRef} role="menu" aria-label={submenuLabel} $open={open}
					onKeyDown={onSubmenuKeyDown} onClick={onSubmenuClick}>
					{children}
				</InlineSubmenu>
			) : (
				<FlyoutLayer ref={flyoutRef} $flip={flip} $open={open}>
					<Panel ref={submenuRef} role="menu" aria-label={submenuLabel}
						onKeyDown={onSubmenuKeyDown} onClick={onSubmenuClick}>
						{children}
					</Panel>
				</FlyoutLayer>
			)}
		</SubmenuRoot>
	);
}

/** Relatively-positioned wrapper anchoring the flyout AND unifying the hover region (the flyout is a
 *  DOM descendant, so the cursor moving from the row into it never leaves this element — no flicker). */
const SubmenuRoot = styled.div`
	position: relative;
	display: block;
`;

/** The parent-row label; grows so the hint + chevron sit flush right. */
const SubmenuLabel = styled.span`
	flex: 1;
	text-align: left;
`;

/** Muted decorative hint before the chevron (e.g. the active locale's flag). */
const SubmenuHint = styled.span`
	font-size: 14px;
	line-height: 1;
	color: ${({theme}) => theme.tokens.ink3};
`;

/** Chevron affordance — points right at the flyout (desktop); rotates down when the inline accordion
 *  is expanded (touch/narrow). */
const Chevron = styled.span<{$down: boolean}>`
	display: inline-flex;
	align-items: center;
	color: ${({theme}) => theme.tokens.ink3};
	transition: transform 0.16s ease;
	transform: rotate(${({$down}) => ($down ? "90deg" : "0deg")});

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`;

/**
 * Absolute side layer holding the flyout {@link Panel}. `left:100%` (+ `padding-left` bridge) puts it
 * flush against the row's right edge with NO dead gap — the transparent bridge belongs to this
 * descendant, so crossing it keeps the cursor inside the hover region. Flips to `right:100%` when the
 * card would overflow the viewport. `top:-6px` lines the flyout's first row up with the parent row.
 */
const FlyoutLayer = styled.div<{$flip: "left" | "right"; $open: boolean}>`
	position: absolute;
	top: -6px;
	z-index: 50;
	/* display:none, NOT visibility:hidden. Out of flow is not out of layout: a hidden box still sits at
	   left:100% of a row in a panel already flush with the viewport's right edge, so it widened the
	   DOCUMENT. The page then scrolled sideways the first time anything called focus() on the bar, and
	   the top bar was measured 178px off-screen (caught by e2e/nav-overflow.spec.ts). The flip
	   measurement below is unaffected: it runs only while open, when this box is laid out again. */
	display: ${({$open}) => ($open ? "block" : "none")};
	${({$flip}) => ($flip === "right" ? "left: 100%; padding-left: 6px;" : "right: 100%; padding-right: 6px;")}
	${({$open}) => ($open ? css`animation: ${panelIn} 0.14s cubic-bezier(0.2, 0.7, 0.2, 1);` : "")}

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

/** Touch/narrow fallback: the rows expand INLINE beneath the parent row (indented with a hairline
 *  rail so they read as nested), never a side flyout — so a 375px row never overflows horizontally. */
const InlineSubmenu = styled.div<{$open: boolean}>`
	display: ${({$open}) => ($open ? "flex" : "none")};
	flex-direction: column;
	gap: 1px;
	margin: 2px 0 2px 14px;
	padding-left: 8px;
	border-left: 2px solid ${({theme}) => theme.tokens.cardBorder};
`;
